import { useEffect, useMemo, useRef, useState } from 'react'
import mapboxgl, { type Map as MapboxMap } from 'mapbox-gl'
import maplibregl from 'maplibre-gl'
import type { FeatureCollection, Point } from 'geojson'
import type { DiscoverBbox, DiscoverRouteListItem } from '@xingroute/schema'
import { api } from '../api/client.js'
import type {
  ActivityFilter,
  DifficultyFilter,
  RouteTypeFilter,
  SortMode,
  StatusFilter,
} from '../hooks/useDiscoverFilters.js'
import { friendlyError } from '../lib/friendlyError.js'
import { useLocale } from '../locale/LocaleContext.js'
import { isSeasonalitySuitableForDate } from './RouteSeasonalityBlock.js'
import { useTheme } from '../theme/ThemeContext.js'
import { useBasemapProvider } from '../hooks/useBasemapProvider.js'
import { clampBbox } from '../lib/bbox.js'
import { getRegionBbox } from '../lib/regionBbox.js'
import { disableMapboxTelemetry } from '../lib/mapboxTelemetry.js'
import { BasemapToggle } from './BasemapToggle.js'
import { buildTiandituStyle } from './tianditu/tiandituStyle.js'

const MAPBOX_TOKEN = (import.meta.env.VITE_MAPBOX_KEY ?? '').trim()
if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN
}

const SOURCE_ID = 'discover-routes'
const CLUSTERS_LAYER = 'discover-route-clusters'
const CLUSTER_COUNT_LAYER = 'discover-route-cluster-count'
const POINT_LAYER = 'discover-route-points'

// The two SDKs share API shape for everything we touch here (Map ctor,
// add/get Source/Layer, cluster expansion, Popup, NavigationControl,
// ScaleControl, AttributionControl, GeoJSONSource). MapLibre 5 is a fork
// of mapbox-gl 1.x; mapbox-gl 3.x keeps the same surface for these calls.
// We type the ref as `MapboxMap` because its types have looser callable
// signatures than maplibre's; at runtime the map is whichever library we
// instantiated (controlled by the `provider` effect dep).
type AnyMap = MapboxMap

interface Props {
  activity: ActivityFilter
  status: StatusFilter
  sort: SortMode
  suitableThisMonth: boolean
  routeType: RouteTypeFilter
  difficulty: DifficultyFilter
  region?: string
  bbox?: DiscoverBbox
  onBboxChange?: (next: DiscoverBbox | undefined) => void
  onCountChange?: (count: number) => void
}

export function DiscoverMapView({
  activity,
  status,
  sort,
  suitableThisMonth,
  routeType,
  difficulty,
  region = '',
  bbox,
  onBboxChange,
  onCountChange,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<AnyMap | null>(null)
  const onBboxChangeRef = useRef(onBboxChange)
  const { t } = useLocale()
  const { effective } = useTheme()
  const { provider } = useBasemapProvider()
  const [routes, setRoutes] = useState<DiscoverRouteListItem[]>([])
  const suitableDate = useMemo(() => new Date(), [])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    onBboxChangeRef.current = onBboxChange
  }, [onBboxChange])

  // DiscoverMapView mirrors the cartographic style of route cards
  // (`RouteThumbMap`) — Mapbox `light-v11` / `dark-v11` matches the
  // static thumbnails in the list view. Under TianDiTu provider the map
  // uses the standard TianDiTu vector + label layers (the closest visual
  // analog at this zoom range). Compliance posture lives in
  // wiki/decisions/05-basemaps.md §Compliance posture.
  //
  // Provider switch re-creates the map (different SDK + style spec).
  // Theme swap stays in-place via `setStyle` for the mapbox provider;
  // TianDiTu has no dark variant so theme has no effect on its tiles.
  useEffect(() => {
    if (!containerRef.current) return
    if (provider === 'mapbox' && !MAPBOX_TOKEN) return
    const lib = provider === 'tianditu' ? maplibregl : mapboxgl

    let map: AnyMap
    if (provider === 'tianditu') {
      const style = buildTiandituStyle('standard')
      if (!style) return // token missing; provider switch will fall back to mapbox via the context guard
      map = new maplibregl.Map({
        container: containerRef.current,
        style,
        center: [104, 35],
        zoom: 3,
        attributionControl: false,
      }) as unknown as MapboxMap
    } else {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: basemapStyleFor(effective),
        center: [104, 35],
        zoom: 3,
        attributionControl: false,
      })
      disableMapboxTelemetry(map)
    }
    mapRef.current = map
    lastThemeRef.current = effective

    // Mapbox / OSM (or TianDiTu) attribution must remain visible per each
    // provider's ToS. The compact AttributionControl on the map itself
    // satisfies this — the global SiteFooter no longer carries the credit.
    map.addControl(
      new lib.AttributionControl({ compact: true }) as unknown as mapboxgl.IControl,
      'bottom-right',
    )
    map.addControl(
      new lib.NavigationControl({ showCompass: true }) as unknown as mapboxgl.IControl,
      'bottom-right',
    )
    map.addControl(
      new lib.ScaleControl({ maxWidth: 120, unit: 'metric' }) as unknown as mapboxgl.IControl,
      'bottom-left',
    )

    // MapLibre's compact AttributionControl auto-opens its <details>
    // element when there is room (jsdom + wide viewports both qualify).
    // Mapbox's equivalent starts collapsed. Force the closed initial
    // state so the two providers feel identical (a small `i` ball that
    // expands only on click). Repeat across a few microtasks because the
    // control is created lazily after the first render frame.
    if (provider === 'tianditu') {
      const closeDetails = () => {
        const details = containerRef.current?.querySelector(
          'details.maplibregl-ctrl-attrib',
        ) as HTMLDetailsElement | null
        if (details) details.open = false
      }
      queueMicrotask(closeDetails)
      requestAnimationFrame(closeDetails)
      setTimeout(closeDetails, 100)
    }

    const publishBbox = () => {
      const b = map.getBounds()
      if (!b) return
      onBboxChangeRef.current?.(clampBbox(b.getWest(), b.getSouth(), b.getEast(), b.getNorth()))
    }

    map.on('load', () => {
      addRouteLayers(map)
      setReady(true)
      publishBbox()
    })
    map.on('moveend', publishBbox)
    map.on('click', (e) => {
      if (!map.getLayer(CLUSTERS_LAYER) || !map.getLayer(POINT_LAYER)) return
      const cluster = map.queryRenderedFeatures(e.point, { layers: [CLUSTERS_LAYER] })[0]
      if (cluster) {
        const clusterId = cluster.properties?.cluster_id
        const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
        if (clusterId == null || !source) return
        const coords = (cluster.geometry as Point).coordinates as [number, number]
        const onZoom = (err: unknown, zoom?: number | null) => {
          if (err || zoom == null) return
          map.easeTo({ center: coords, zoom })
        }
        // mapbox-gl 3.x uses callback signature; maplibre 5.x returns a
        // Promise from the same method. Detect at runtime.
        const result = source.getClusterExpansionZoom(Number(clusterId), onZoom)
        if (result && typeof (result as unknown as Promise<number>).then === 'function') {
          ;(result as unknown as Promise<number>)
            .then((z) => onZoom(null, z))
            .catch((err) => onZoom(err))
        }
        return
      }
      const point = map.queryRenderedFeatures(e.point, { layers: [POINT_LAYER] })[0]
      if (!point) return
      const props = point.properties as Record<string, string | number | undefined>
      const coords = (point.geometry as Point).coordinates as [number, number]
      new lib.Popup({ offset: 18, closeButton: false })
        .setDOMContent(routePopup(props))
        .setLngLat(coords)
        .addTo(map as never)
    })
    map.on('mousemove', (e) => {
      if (!map.getLayer(CLUSTERS_LAYER) || !map.getLayer(POINT_LAYER)) return
      const hit = map.queryRenderedFeatures(e.point, { layers: [CLUSTERS_LAYER, POINT_LAYER] })[0]
      map.getCanvas().style.cursor = hit ? 'pointer' : ''
    })
    return () => {
      map.remove()
      mapRef.current = null
      setReady(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider])

  // Theme swap: setStyle reuses the existing camera but wipes
  // user-added sources / layers, so we re-add ours on `style.load`. The
  // GeoJSON source data is re-pushed by the geojson effect below once
  // `ready` flips back. TianDiTu has no dark variant so we skip this
  // effect when provider=tianditu.
  const lastThemeRef = useRef<'light' | 'dark'>(effective)
  useEffect(() => {
    if (provider !== 'mapbox') return
    const map = mapRef.current as MapboxMap | null
    if (!map) return
    if (lastThemeRef.current === effective) return
    lastThemeRef.current = effective
    setReady(false)
    map.setStyle(basemapStyleFor(effective))
    map.once('style.load', () => {
      addRouteLayers(map)
      setReady(true)
    })
  }, [effective, provider])

  // Fly to region bbox when a region filter is selected
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    const bbox = getRegionBbox(region)
    if (!bbox) return
    const [west, south, east, north] = bbox
    map.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { padding: 40, duration: 1200 },
    )
  }, [region, ready])

  useEffect(() => {
    if (!bbox) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .listRoutes({
        bbox,
        status: status === 'all' ? undefined : status,
        limit: 200,
        includeSeasonality: suitableThisMonth,
        activity: activity === 'all' ? undefined : activity,
        difficulty: difficulty === 'all' ? undefined : difficulty,
        routeType: routeType === 'all' ? undefined : routeType,
        region: region || undefined,
        sort,
      })
      .then((items) => {
        if (cancelled) return
        setRoutes(
          sortRoutes(
            filterRoutes(
              items,
              activity,
              region,
              routeType,
              difficulty,
              suitableThisMonth,
              suitableDate,
            ),
            sort,
          ),
        )
      })
      .catch((err) => {
        if (cancelled) return
        console.warn('[DiscoverMapView] failed to load routes for bbox', err)
        setError(friendlyError(err, t))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [
    activity,
    bbox,
    difficulty,
    region,
    routeType,
    sort,
    status,
    suitableDate,
    suitableThisMonth,
    t,
  ])

  const geojson = useMemo(() => routesToGeojson(routes), [routes])

  useEffect(() => {
    onCountChange?.(routes.length)
    const map = mapRef.current
    if (!map || !ready) return
    const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    source?.setData(geojson)
  }, [geojson, onCountChange, ready, routes.length])

  if (provider === 'mapbox' && !MAPBOX_TOKEN) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] text-sm text-[var(--xr-text-muted)]">
        {t('discover.mapTokenMissing')}
      </div>
    )
  }

  return (
    <div className="xr-map-chrome relative h-[calc(100svh-7.5rem)] overflow-hidden bg-[var(--xr-bg-surface-elev)] sm:rounded-[var(--xr-radius-lg)] sm:border sm:border-[var(--xr-border-default)] lg:h-full lg:min-h-0">
      <div ref={containerRef} className="h-full w-full" />
      <div className="pointer-events-none absolute left-3 top-3 z-10 flex flex-col gap-2">
        <div className="rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)]/95 px-3 py-2 text-xs text-[var(--xr-text-secondary)] shadow-[var(--xr-shadow-sm)] backdrop-blur">
          {loading
            ? t('discover.mapLoading')
            : t('discover.mapCount').replace('{{count}}', String(routes.length))}
        </div>
        {error && (
          <div className="rounded-[var(--xr-radius-md)] border border-[var(--xr-danger)]/30 bg-[var(--xr-danger-soft)] px-3 py-2 text-xs text-[var(--xr-danger)] shadow-[var(--xr-shadow-sm)]">
            {error}
          </div>
        )}
      </div>
      <div className="pointer-events-auto absolute right-3 top-3 z-10">
        <BasemapToggle />
      </div>
    </div>
  )
}

function addRouteLayers(map: AnyMap): void {
  map.addSource(SOURCE_ID, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
    cluster: true,
    clusterRadius: 50,
  })
  map.addLayer({
    id: CLUSTERS_LAYER,
    type: 'circle',
    source: SOURCE_ID,
    filter: ['has', 'point_count'],
    paint: {
      // Cluster colour ramp uses Brand Moss (chrome) since clusters are an
      // overlay UI affordance, not a "trail". Single-route points below use
      // Accent Twilight to match the trail polyline colour. Ramp:
      // <10 = light Moss, 10–30 = Moss, ≥30 = deep Moss hover.
      'circle-color': ['step', ['get', 'point_count'], '#349e7c', 10, '#1a5a44', 30, '#134836'],
      'circle-radius': ['step', ['get', 'point_count'], 18, 10, 22, 30, 28],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
    },
  } as never)
  map.addLayer({
    id: CLUSTER_COUNT_LAYER,
    type: 'symbol',
    source: SOURCE_ID,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-size': 12,
      'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
    },
    paint: { 'text-color': '#ffffff' },
  } as never)
  map.addLayer({
    id: POINT_LAYER,
    type: 'circle',
    source: SOURCE_ID,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': '#e66725',
      'circle-radius': 7,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
    },
  } as never)
}

function basemapStyleFor(effective: 'light' | 'dark'): string {
  // Matches RouteThumbMap.styleSlugFor for visual continuity between
  // list-card thumbnails and the interactive Discover map.
  return effective === 'dark'
    ? 'mapbox://styles/mapbox/dark-v11'
    : 'mapbox://styles/mapbox/light-v11'
}

function routesToGeojson(routes: DiscoverRouteListItem[]): FeatureCollection<Point> {
  return {
    type: 'FeatureCollection',
    features: routes
      .filter((route) => route.map_center)
      .map((route) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [route.map_center!.lon, route.map_center!.lat],
        },
        properties: {
          id: route.id,
          title: route.title,
          slug: route.slug,
          status: route.status,
          activities: route.activities.join(' · '),
          distance: route.distance_km == null ? '' : `${route.distance_km.toFixed(1)} km`,
        },
      })),
  }
}

function filterRoutes(
  routes: DiscoverRouteListItem[],
  activity: ActivityFilter,
  region: string,
  routeType: RouteTypeFilter,
  difficulty: DifficultyFilter,
  suitableThisMonth: boolean,
  suitableDate: Date,
): DiscoverRouteListItem[] {
  const wantedRegion = region.trim().toLocaleLowerCase()
  return routes.filter((route) => {
    const acts = route.activities
    const activityOk = activity === 'all' || acts.includes(activity)
    const routeTypeOk = routeType === 'all' || route.route_type === routeType
    const difficultyOk = difficulty === 'all' || route.difficulty === difficulty
    const regionOk =
      !wantedRegion ||
      Object.values(route.region ?? {}).some(
        (value) => value?.trim().toLocaleLowerCase() === wantedRegion,
      )
    const seasonalityOk =
      !suitableThisMonth || isSeasonalitySuitableForDate(route.seasonality, suitableDate)
    return activityOk && routeTypeOk && difficultyOk && regionOk && seasonalityOk
  })
}

function sortRoutes(routes: DiscoverRouteListItem[], sort: SortMode): DiscoverRouteListItem[] {
  const sorted = [...routes]
  if (sort === 'distance') sorted.sort((a, b) => (b.distance_km ?? 0) - (a.distance_km ?? 0))
  else if (sort === 'ascent') sorted.sort((a, b) => (b.ascent_m ?? 0) - (a.ascent_m ?? 0))
  return sorted
}

function routePopup(props: Record<string, string | number | undefined>): HTMLElement {
  const root = document.createElement('div')
  root.className = 'min-w-44'
  const title = document.createElement('a')
  title.href = `/r/${String(props.slug ?? '')}`
  title.className = 'block text-sm font-semibold text-[var(--xr-brand-primary)]'
  title.textContent = String(props.title ?? 'Untitled route')
  const meta = document.createElement('p')
  meta.className = 'mt-1 text-xs text-[var(--xr-text-secondary)]'
  meta.textContent = [props.activities, props.distance].filter(Boolean).join(' · ')
  root.append(title, meta)
  return root
}
